import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useMessage } from '@ott/message';
import { authActions } from '@ott/solar-auth';

import { getAuthSelector } from 'src/redux/selectors/commonSelectors';

import { isUserAuthenticated } from 'src/helpers/user';
import useIsWebview from 'src/hooks/useIsWebview';
import useTypedSelector from 'src/hooks/useTypedSelector';

import { motion } from 'framer-motion';

import styles from './Bubble.scss';

interface Props {
  x: number;
  y: number;
  radius: number;
  icon: JSX.Element;
  bubbleClick: (() => void) | null;
  activeCircle: { x: number; y: number; radius: number } | null;
  setActiveCircle: (state: { x: number; y: number; radius: number } | null) => void;
}

export const Bubble = ({
  x,
  y,
  radius,
  icon,
  bubbleClick,
  activeCircle,
  setActiveCircle,
}: Props) => {
  const [active, setActive] = useState(false);
  const [deltaX, setDeltaX] = useState(0);
  const [deltaY, setDeltaY] = useState(0);
  const delta = 0.2;
  const ms = useMessage();
  const auth = useTypedSelector(getAuthSelector);
  const dispatch = useDispatch();
  const isWebview = useIsWebview();
  const inviteClick = () => {
    if (isUserAuthenticated(auth) || isWebview) {
      if (bubbleClick) {
        return bubbleClick();
      } else {
        ms.open('Something went wrong', {
          style: { backgroundColor: '#e74c3c', color: 'white' },
        });
      }
    } else {
      dispatch(authActions.showAuth());
    }
  };

  useEffect(() => {
    if (activeCircle !== null) {
      if (!active) {
        setDeltaX(
          (delta * (activeCircle.radius * (x - activeCircle.x))) / (activeCircle.radius + radius)
        );
        setDeltaY(
          (delta * (activeCircle.radius * (y - activeCircle.y))) / (activeCircle.radius + radius)
        );
      }
    } else {
      setDeltaX(0);
      setDeltaY(0);
    }
  }, [activeCircle]);

  return (
    <motion.div
      onMouseOver={() => {
        setActive(true);
        setActiveCircle({ x: x, y: y, radius: radius });
      }}
      onMouseLeave={() => {
        setActive(false);
        setActiveCircle(null);
      }}
      onClick={() => inviteClick()}
      whileHover={{ scale: 1 + delta }}
      className={styles.bubble}
      animate={{
        x: x - radius + deltaX,
        y: y - radius + deltaY,
      }}
      style={{
        width: radius * 2,
        height: radius * 2,
        borderRadius: radius,
      }}
      transition={{ type: 'spring', delay: 0.1 }}
    >
      <div className={styles.container}>
        <div className={styles.icon}>{icon}</div>
      </div>
    </motion.div>
  );
};
